// src/components/tournament/MatchBox.js

import React, { useState, useMemo, useEffect } from "react";
import DraggablePlayerSlot from "../common/DraggablePlayerSlot";
import ScoreEntryModal from "../common/ScoreEntryModal";
import { canRearrangePlayers } from "../../utils/dragDropUtils";
import { resolvePlaceholderThroughByes } from "../../utils/doubleEliminationUtils";
import { getMatchRaceTarget } from "../../utils/raceUtils";

function MatchBox({
    match,
    position,
    updateMatch,
    startMatch,
    unstartMatch,
    markFinished,
    undoFinished,
    finalizeSingleBye,
    bracket,
    fullBracket = null, // Full bracket including hidden BYE matches (for resolving placeholders)
    onSwapPlayers,
    readOnly = false,
    onViewProfile = null,
    playerUserIdMap = null,
    selfScoringEnabled = false,
    hideBYEMatches = false, // Whether BYE matches are hidden (triggers placeholder resolution)
    onMatchDetailsClick = null, // Optional callback for viewing finished match details (used in public viewer)
    isDoubles = false, // If true, abbreviate last names; if false (singles), show full names
    raceSettings = null // { winnersRaceLength, losersRaceLength } — caps score entry
}) {
    const [showScoreModal, setShowScoreModal] = useState(false);
    const [showSmsSentPopup, setShowSmsSentPopup] = useState(false);
    const { p1, p2, score1, score2, finished, matchNum, table, winner, p1LiveScore, p2LiveScore } = match;

    // Local editing buffer for the table field so a multi-digit number (e.g. "17")
    // can be fully typed before validation/commit. Committing per-keystroke would
    // reject the intermediate "1" if table 1 is in use. Re-sync when the committed
    // value changes externally (assignment, reshuffle, sync).
    const [tableInput, setTableInput] = useState(table ?? '');
    useEffect(() => { setTableInput(table ?? ''); }, [table]);

    const commitTable = () => {
        const next = tableInput.trim();
        if (next === (table ?? '')) return;
        const ok = updateMatch(match.id, { table: next });
        if (ok === false) setTableInput(table ?? ''); // rejected — revert to committed value
    };

    // Use live score if available and match is in progress, otherwise use final score
    // With player-specific scoring, prefer the most recent update from either player
    const getLiveScore = () => {
        if (!match.started || finished) return null;
        // If both players have submitted, they should have the same scores
        // Use p1's view first, fall back to p2's
        if (p1LiveScore) return p1LiveScore;
        if (p2LiveScore) return p2LiveScore;
        return null;
    };
    const liveScore = getLiveScore();
    const displayScore1 = liveScore ? liveScore.p1 : score1;
    const displayScore2 = liveScore ? liveScore.p2 : score2;

    // Auto-hide SMS sent popup after 2.5 seconds
    useEffect(() => {
        if (showSmsSentPopup) {
            const timer = setTimeout(() => setShowSmsSentPopup(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [showSmsSentPopup]);

    // Inject fadeInOut animation CSS if not already present
    useEffect(() => {
        const styleId = 'matchbox-sms-animation';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                @keyframes fadeInOut {
                    0% { opacity: 0; transform: translateX(-50%) translateY(10px); }
                    15% { opacity: 1; transform: translateX(-50%) translateY(0); }
                    85% { opacity: 1; transform: translateX(-50%) translateY(0); }
                    100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // Resolve placeholder names through hidden BYE matches for better UX
    // e.g., "Winner of M16" becomes "Loser of M2" if M16 is a hidden BYE match
    const resolvedP1Name = useMemo(() => {
        if (hideBYEMatches && fullBracket && p1?.name) {
            return resolvePlaceholderThroughByes(p1.name, fullBracket);
        }
        return p1?.name;
    }, [hideBYEMatches, fullBracket, p1?.name]);

    const resolvedP2Name = useMemo(() => {
        if (hideBYEMatches && fullBracket && p2?.name) {
            return resolvePlaceholderThroughByes(p2.name, fullBracket);
        }
        return p2?.name;
    }, [hideBYEMatches, fullBracket, p2?.name]);

    // Check if this is a BYE match that can be finalized
    const isByeMatch = p1.name === "BYE" || p2.name === "BYE";
    const canFinalizeBye = (
        !finished &&
        isByeMatch &&
        finalizeSingleBye && // Only if individual BYE finalization is available
        ((p1.name === "BYE" && p2.name && p2.name !== "BYE" && !p2.name.includes("Winner of") && !p2.name.includes("Loser of")) ||
         (p2.name === "BYE" && p1.name && p1.name !== "BYE" && !p1.name.includes("Winner of") && !p1.name.includes("Loser of")))
    );

    // Check if match has pending players (references to other matches)
    const hasPendingPlayers = (
        (p1.name && (p1.name.startsWith("Winner of") || p1.name.startsWith("Loser of"))) ||
        (p2.name && (p2.name.startsWith("Winner of") || p2.name.startsWith("Loser of")))
    );

    // Determine match state for styling
    const getMatchState = () => {
        if (finished) return 'finished';
        if (match.started) return 'playing';

        // Check if match has both players (not pending, not BYE, not empty)
        const hasBothPlayers = (
            p1 && p2 &&
            typeof p1.name === "string" &&
            typeof p2.name === "string" &&
            !["Winner of", "Loser of"].some(prefix => p1.name.startsWith(prefix) || p2.name.startsWith(prefix)) &&
            !["BYE"].includes(p1.name) &&
            !["BYE"].includes(p2.name) &&
            p1.name && p2.name
        );

        if (hasBothPlayers) return 'waiting';
        return 'pending';
    };

    const matchState = getMatchState();

    // Get accent color based on match state (for left border)
    const getAccentColor = () => {
        switch (matchState) {
            case 'finished': return 'var(--match-finished)';
            case 'playing': return 'var(--match-playing)';
            case 'waiting': return 'var(--match-waiting)';
            default: return 'var(--match-pending)';
        }
    };

    // Background color - constant for cleaner look
    const backgroundColor = 'var(--bg-card)';

    // Subtle shadow for every state. Playing matches are indicated by the left
    // accent bar and the "Playing" label — no blue glow/halo around the box.
    const getBoxShadow = () => 'var(--shadow-sm)';

    const renderPlayerLabel = (player, playerSlot) => {
        // Get resolved name if BYE matches are hidden (e.g., "Winner of M16" -> "Loser of M2")
        const displayName = playerSlot === 'p1' ? resolvedP1Name : resolvedP2Name;

        // Check if this is a placeholder (winner/loser reference, BYE, or empty)
        const isPlaceholder = typeof displayName !== "string" ||
            displayName.startsWith("Winner of") ||
            displayName.startsWith("Loser of") ||
            displayName === "BYE" ||
            !displayName;

        if (isPlaceholder) {
            return (
                <span style={{
                    color: "var(--text-secondary)",
                    fontSize: 11,
                    fontStyle: 'italic',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                    display: 'block'
                }}>
                    {displayName || "TBD"}
                </span>
            );
        }

        // Draggable player name for first round matches that can be rearranged (excluding BYE)
        const canRearrange = !readOnly && match.round === 1 && bracket && canRearrangePlayers(bracket);
        const isWinner = finished && winner === player.name;
        const isLoser = finished && winner && winner !== player.name;

        // Get player's user ID from the map if available
        const playerUserId = playerUserIdMap?.get(player.name)?.odUserId || null;

        return (
            <DraggablePlayerSlot
                playerName={player.name}
                isWinner={isWinner}
                isLoser={isLoser}
                isFinished={finished}
                onSwapPlayers={readOnly ? () => {} : onSwapPlayers}
                matchId={match.id}
                playerSlot={playerSlot}
                canRearrange={canRearrange}
                onViewProfile={onViewProfile}
                playerUserId={playerUserId}
                isDoubles={isDoubles}
                style={{
                    fontSize: 11,
                    minHeight: 16,
                    lineHeight: '16px',
                    textAlign: 'center'
                }}
            />
        );
    };

    // Determine winner highlighting (removed background, just font weight)
    const getPlayerStyle = (playerName) => {
        if (!finished) return {};

        const isWinner = winner === playerName;
        return {
            fontWeight: isWinner ? 'bold' : 'normal'
        };
    };

    const handleScoreUpdate = ({ matchId, score1, score2, winner }) => {
        // If both scores are 0 (no scores entered), set winner's score to 1 to avoid tie validation
        const finalScore1 = (Number(score1) === 0 && Number(score2) === 0)
            ? (winner === match.p1.name ? "1" : "0")
            : score1;
        const finalScore2 = (Number(score1) === 0 && Number(score2) === 0)
            ? (winner === match.p2.name ? "1" : "0")
            : score2;

        // Update scores
        updateMatch(matchId, { score1: finalScore1, score2: finalScore2 });

        // Mark as finished with the winner
        const updatedMatch = {
            ...match,
            score1: finalScore1,
            score2: finalScore2
        };
        markFinished(updatedMatch);
    };

    const handleMatchClick = () => {
        // In readOnly mode, allow viewing match details for finished or in-progress matches
        if (readOnly) {
            if ((finished || match.started) && onMatchDetailsClick) {
                onMatchDetailsClick(match);
            }
            return;
        }
        if (hasPendingPlayers) return;
        if (canFinalizeBye) {
            finalizeSingleBye(match);
            return;
        }
        // Only open score modal if match has started or is finished
        if (!match.started && !finished) return;
        setShowScoreModal(true);
    };

    return (
        <>
            <div
                className={`match-box match-box-${matchState}`}
                style={{
                    position: "absolute",
                    left: position.x,
                    top: position.y,
                    width: 168,
                    height: 56,
                    zIndex: 1,
                    backgroundColor: backgroundColor,
                    border: matchState === 'pending' ? '2px dashed var(--text-muted)' : '1px solid var(--border-color)',
                    borderLeft: `5px solid ${getAccentColor()}`,
                    borderRadius: 'var(--radius-md)',
                    padding: '6px 8px',
                    boxShadow: getBoxShadow(),
                    cursor: (readOnly && finished && onMatchDetailsClick) ? "pointer" : (readOnly || hasPendingPlayers ? "default" : "pointer"),
                    fontSize: 11,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'all 0.25s ease',
                    fontFamily: 'var(--font-body)',
                    overflow: 'visible'
                }}
                onMouseEnter={(e) => {
                    const isClickable = readOnly
                        ? (finished && onMatchDetailsClick)
                        : (match.started || finished || matchState === 'waiting');
                    if (isClickable) {
                        e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                    }
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = getBoxShadow();
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                }}
                onClick={handleMatchClick}
            >
                {/* SMS Sent Popup - shows briefly when director starts a match with SMS enabled */}
                {showSmsSentPopup && (
                    <div
                        style={{
                            position: 'absolute',
                            top: -36,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'var(--success)',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: 11,
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            zIndex: 100,
                            boxShadow: 'var(--shadow-md)',
                            animation: 'fadeInOut 2.5s ease-in-out'
                        }}
                    >
                        Text Alerts Sent
                    </div>
                )}

                {/* Waiting state hover overlay - positioned to only cover right portion */}
                {matchState === 'waiting' && !readOnly && (
                    <div
                        className="start-match-overlay"
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 126, // Only cover right ~42px (quarter of 168px width)
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
                            borderRadius: '0 var(--radius-md) var(--radius-md) 0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            cursor: 'pointer',
                            zIndex: 10
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                        onClick={(e) => {
                            e.stopPropagation();
                            startMatch(match.id);
                            // Show SMS sent popup if self-scoring is enabled and match has tokens
                            if (selfScoringEnabled && match.p1Token && match.p2Token) {
                                setShowSmsSentPopup(true);
                            }
                        }}
                    >
                        <span style={{
                            color: 'var(--text-on-accent)',
                            fontFamily: 'var(--font-display)',
                            fontWeight: 700,
                            fontSize: 11,
                            letterSpacing: '0.02em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <span style={{ fontSize: 14 }}>▶</span>
                        </span>
                    </div>
                )}

                {/* Row 1: Match number, Player 1, Score 1 */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    ...getPlayerStyle(p1.name)
                }}>
                    {/* Match Number Badge */}
                    <div style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 10,
                        fontWeight: 700,
                        color: 'var(--text-muted)',
                        backgroundColor: 'var(--bg-tertiary)',
                        padding: '2px 5px',
                        borderRadius: 'var(--radius-sm)',
                        flexShrink: 0,
                        letterSpacing: '0.02em'
                    }}>
                        M{matchNum}
                    </div>

                    <div style={{ flex: 1, minWidth: 0, maxWidth: 110 }}>
                        {renderPlayerLabel(p1, 'p1')}
                    </div>

                    {/* Score Display - Scoreboard style */}
                    <div style={{
                        minWidth: 24,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 700,
                        fontFamily: 'var(--font-mono)',
                        color: finished && winner === p1.name ? 'var(--text-primary)' : 'var(--text-primary)',
                        flexShrink: 0,
                        backgroundColor: finished && winner === p1.name ? 'var(--gold)' : 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-sm)',
                        fontVariantNumeric: 'tabular-nums'
                    }}>
                        {displayScore1 ?? score1 ?? '-'}
                    </div>
                </div>

                {/* Row 2: Table number, Player 2, Score 2 */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    ...getPlayerStyle(p2.name)
                }}>
                    {/* Table Number - fixed width to align with match number badge above */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        minWidth: 28, // Match the approximate width of match number badge (M1, M2, etc.)
                        fontFamily: 'var(--font-mono)',
                        fontVariantNumeric: 'slashed-zero', // distinguish table "0" from "8"
                        fontSize: 10,
                        backgroundColor: 'var(--bg-tertiary)',
                        padding: '2px 5px',
                        borderRadius: 'var(--radius-sm)',
                        boxSizing: 'border-box'
                    }}>
                        <span style={{ color: 'var(--text-muted)', fontWeight: 600, marginRight: 1 }}>T</span>
                        {readOnly || finished || match.started ? (
                            <span style={{
                                fontWeight: 700,
                                color: 'var(--text-primary)',
                                minWidth: 10,
                                textAlign: 'center'
                            }}>
                                {table || '-'}
                            </span>
                        ) : (
                            <input
                                style={{
                                    width: 14,
                                    fontSize: 10,
                                    border: '1px solid var(--border-color)',
                                    borderRadius: 3,
                                    textAlign: 'center',
                                    padding: '0px',
                                    fontWeight: 700,
                                    fontFamily: 'var(--font-mono)',
                                    backgroundColor: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    outline: 'none',
                                    transition: 'border-color 0.2s ease'
                                }}
                                value={tableInput}
                                onChange={e => {
                                    e.stopPropagation();
                                    // Buffer keystrokes only; validation/commit
                                    // happens on blur/Enter so multi-digit numbers
                                    // can be fully typed. The in-progress table
                                    // guard lives in updateMatch (fresh state).
                                    setTableInput(e.target.value);
                                }}
                                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                onClick={e => e.stopPropagation()}
                                onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
                                onBlur={e => { e.target.style.borderColor = 'var(--border-color)'; commitTable(); }}
                                maxLength={2}
                            />
                        )}
                    </div>

                    <div style={{ flex: 1, minWidth: 0, maxWidth: 110 }}>
                        {renderPlayerLabel(p2, 'p2')}
                    </div>

                    {/* Score Display */}
                    <div style={{
                        minWidth: 24,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 700,
                        fontFamily: 'var(--font-mono)',
                        color: finished && winner === p2.name ? 'var(--text-primary)' : 'var(--text-primary)',
                        flexShrink: 0,
                        backgroundColor: finished && winner === p2.name ? 'var(--gold)' : 'var(--bg-tertiary)',
                        borderRadius: 'var(--radius-sm)',
                        fontVariantNumeric: 'tabular-nums'
                    }}>
                        {displayScore2 ?? score2 ?? '-'}
                    </div>
                </div>


            </div>

            {showScoreModal && (
                <ScoreEntryModal
                    match={match}
                    onClose={() => setShowScoreModal(false)}
                    onSubmit={handleScoreUpdate}
                    onUndo={undoFinished}
                    onUnstart={unstartMatch}
                    readOnly={readOnly}
                    raceTarget={getMatchRaceTarget(match, raceSettings)}
                />
            )}
        </>
    );
}

// Custom equality so a single match update doesn't re-render every box. Match objects
// get a fresh identity on every action (the bracket is deep-cloned during propagation),
// so we compare the fields that actually affect THIS box's output rather than identity.
// `bracket`/`fullBracket` also change identity every action but only matter here for
// round-1 drag gating and BYE-placeholder resolution (the latter is driven by p1/p2
// names, already compared; the hidden-BYE structure is static after build).
function matchBoxPropsEqual(prev, next) {
    if (prev.position?.x !== next.position?.x || prev.position?.y !== next.position?.y) return false;

    if (
        prev.readOnly !== next.readOnly ||
        prev.raceSettings !== next.raceSettings ||
        prev.selfScoringEnabled !== next.selfScoringEnabled ||
        prev.hideBYEMatches !== next.hideBYEMatches ||
        prev.isDoubles !== next.isDoubles ||
        prev.onViewProfile !== next.onViewProfile ||
        prev.onMatchDetailsClick !== next.onMatchDetailsClick ||
        prev.onSwapPlayers !== next.onSwapPlayers ||
        prev.playerUserIdMap !== next.playerUserIdMap ||
        prev.updateMatch !== next.updateMatch ||
        prev.startMatch !== next.startMatch ||
        prev.unstartMatch !== next.unstartMatch ||
        prev.markFinished !== next.markFinished ||
        prev.undoFinished !== next.undoFinished ||
        prev.finalizeSingleBye !== next.finalizeSingleBye
    ) return false;

    const a = prev.match || {};
    const b = next.match || {};
    if (
        a.id !== b.id ||
        a.matchNum !== b.matchNum ||
        a.table !== b.table ||
        a.score1 !== b.score1 ||
        a.score2 !== b.score2 ||
        a.finished !== b.finished ||
        a.started !== b.started ||
        a.winner !== b.winner ||
        a.status !== b.status ||
        a.round !== b.round ||
        a.p1?.name !== b.p1?.name ||
        a.p2?.name !== b.p2?.name ||
        a.p1Token !== b.p1Token ||
        a.p2Token !== b.p2Token ||
        a.p1LiveScore !== b.p1LiveScore ||
        a.p2LiveScore !== b.p2LiveScore
    ) return false;

    if ((a.round === 1 || b.round === 1) && prev.bracket !== next.bracket) {
        const prevRearrange = prev.bracket ? canRearrangePlayers(prev.bracket) : false;
        const nextRearrange = next.bracket ? canRearrangePlayers(next.bracket) : false;
        if (prevRearrange !== nextRearrange) return false;
    }

    return true;
}

export default React.memo(MatchBox, matchBoxPropsEqual);
